// Main render loop: chunk cache blits, live territory/water/entity/effect
// layers, day-night grade, viewport culling — 60fps. Owner: client-render.

import {
  CHUNK, DIRS, SQRT3, chunkKey, lerp, neighbors,
} from '../../shared/hex'
import { TERRAIN, Terrain, isPassable } from '../../shared/terrain'
import {
  BUILDINGS, DAY_TICKS, FILL_MAX_TIER, GUARD_RANGE, ITEMS, TICK_MS,
  buildingFootprint, isBoxOrder, isTerraform,
} from '../../shared/constants'
import { buildingClaimTiles } from '../../shared/territory'
import type { Building, BuildingType, GameEvent, Resource, UnitWire } from '../../shared/types'
import { BUILD_ICON, RES_ICON, starvingHours, unitActivity } from '../ui/panels'
import { statusIcon } from './status'
import { drawStatusBadge } from './statusBadge'
import { missingWorkTool } from './workerTool'
import { drawToolWarning } from './toolWarning'
import type { ClientMsg } from '../../shared/protocol'
import { HEX_SIZE, type GameState } from '../state'
import { assignUnitSlots } from '../slots'
import {
  bucketScale, chunkAt, drawWaterShimmer, elevAt, elevLift, GROUND_TILT, getChunkBitmap, hexEdge, hexPathAt,
  HEX_TAU6, camAngle, hexToPixel, ownerAt, pruneChunkCache, roadAt, rotXY, setViewRot, sitePrepPhase, siteStageIcon, terrainAt, treeAt, viewRotate, viewUnrotate,
} from './terrain'
import { levelTarget, townhallGroundReason, townhallHexHint } from '../../shared/elevation'
import { buildingMaxHp, townHallPhase } from '../../shared/townhall'
import {
  drawBuilding, drawBuildingOverlay, drawFarmField, drawGearSwap, drawGhost, drawRallyFlag,
  drawSelectionRing, drawUnit, pierArtAngle,
} from './entities'
import { resolvePier } from '../pier'
import { rampDir } from '../ramp'
import type { ItemType } from '../../shared/types'
import { initMinimap } from './minimap'
import { clampCamera, drawFog, fogOf, fogVisible } from './fog'
import { pickEntity } from '../pick'
import { storageStatus } from '../storage'
import { drawStorageWarning } from './storageWarnings'
import { drawDiscoveries } from './discoveries'
import { drawBuildPreviewCard, placementPreview } from './buildPreviewCard'
import { drawTreeGrowthBadge, treeGrowthStage } from './treeGrowth'

const S = HEX_SIZE
const TAU = Math.PI * 2
// The town hall art is drawn this much larger than a normal building so the capital
// spills a little onto its 7-hex plaza (neighbour hex centres sit at √3·S ≈ 1.73·S).
const TOWNHALL_ART_SCALE = 1.7
// Highest point of each phase's upright art, including its flagpole, in hex sizes.
const TOWNHALL_WARNING_HEIGHT = [0.4, 0.75, 1.11, 1.28, 1.49] as const

// ---- hover silhouette -------------------------------------------------------------
// Trace a thin outline around whatever the cursor would select. The entity's art is
// rendered once into an offscreen buffer, flattened to a flat highlight colour, then
// stamped at a ring of small offsets to peek out past the art as a crisp outline —
// works for any vector art (units, buildings) without per-shape code. The offset is
// kept constant in *screen* pixels (÷ zoom) so it never bloats when zoomed in.
const SIL_SS = 4 // offscreen supersample: px per world-unit (crisp up to ~4x zoom)
const SIL_HALF = S * 2.6 // half of the capture box in world units (fits the 1.7x hall)
const SIL_WIDTH_PX = 2.6 // outline thickness, in screen px
const SIL_COLOR = 'rgba(255, 250, 232, 0.95)'
const SIL_RING: readonly (readonly [number, number])[] = [
  [1, 0], [-1, 0], [0, 1], [0, -1],
  [0.71, 0.71], [-0.71, 0.71], [0.71, -0.71], [-0.71, -0.71],
]
let silCanvas: HTMLCanvasElement | null = null
/** Draw a thin hover outline around `draw` (called at the current translated origin). */
function drawHoverSilhouette(ctx: CanvasRenderingContext2D, z: number, draw: (c: CanvasRenderingContext2D) => void): void {
  if (!silCanvas) {
    silCanvas = document.createElement('canvas')
    const px = Math.ceil(SIL_HALF * 2 * SIL_SS)
    silCanvas.width = px
    silCanvas.height = px
  }
  const oc = silCanvas.getContext('2d')
  if (!oc) return
  oc.setTransform(1, 0, 0, 1, 0, 0)
  oc.clearRect(0, 0, silCanvas.width, silCanvas.height)
  oc.save()
  oc.translate(silCanvas.width / 2, silCanvas.height / 2)
  oc.scale(SIL_SS, SIL_SS)
  draw(oc)
  oc.restore()
  // Flatten whatever the art painted into a single highlight colour (its own fills
  // become the mask), then reset for the next entity.
  oc.globalCompositeOperation = 'source-in'
  oc.fillStyle = SIL_COLOR
  oc.fillRect(0, 0, silCanvas.width, silCanvas.height)
  oc.globalCompositeOperation = 'source-over'
  // Stamp the mask around a small ring so a thin rim peeks out past the art. The
  // real art draws on top afterwards, leaving just the rim.
  const out = SIL_WIDTH_PX / z
  const box = SIL_HALF * 2
  for (const [rx, ry] of SIL_RING) {
    ctx.drawImage(silCanvas, -SIL_HALF + rx * out, -SIL_HALF + ry * out, box, box)
  }
}

// ---- viewport ---------------------------------------------------------------------

function viewSize(canvas: HTMLCanvasElement): { cw: number; ch: number } {
  return {
    cw: canvas.clientWidth || canvas.width || 800,
    ch: canvas.clientHeight || canvas.height || 600,
  }
}

/** Chunk keys overlapping the viewport plus a 1-chunk margin. Exported contract. */
export function visibleChunkKeys(st: GameState, canvas: HTMLCanvasElement): string[] {
  const { cw, ch } = viewSize(canvas)
  const z = st.cam.zoom
  const mx = S * SQRT3 * CHUNK // 1-chunk margin, world px
  const my = S * 1.5 * CHUNK
  // Screen-box half-extents (screen-aligned), then inverse-rotate the four corners to
  // world and take their AABB — when the view is spun the visible region is a rotated
  // box, so an axis-aligned box around cam would miss the corners. Identity at rot 0.
  const hx = cw / (2 * z) + mx
  const hy = ch / (2 * z) + my
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity
  for (const ox of [-hx, hx]) for (const oy of [-hy, hy]) {
    const w = viewUnrotate(st, ox, oy)
    const wx = st.cam.x + w.x, wy = st.cam.y + w.y
    if (wx < minX) minX = wx; if (wx > maxX) maxX = wx
    if (wy < minY) minY = wy; if (wy > maxY) maxY = wy
  }
  const rMin = Math.floor(minY / (S * 1.5)) - 1
  const rMax = Math.ceil(maxY / (S * 1.5)) + 1
  const out: string[] = []
  for (let cy = Math.floor(rMin / CHUNK); cy <= Math.floor(rMax / CHUNK); cy++) {
    const r0 = cy * CHUNK
    const r1 = r0 + CHUNK - 1
    // q = x/(S*sqrt3) - r/2 → widest q range across this chunk row
    const qMin = minX / (S * SQRT3) - r1 / 2 - 1
    const qMax = maxX / (S * SQRT3) - r0 / 2 + 1
    for (let cx = Math.floor(qMin / CHUNK); cx <= Math.floor(qMax / CHUNK); cx++) {
      out.push(chunkKey(cx, cy))
    }
  }
  return out
}

// ---- day/night grade -----------------------------------------------------------------

interface Grade { r: number; g: number; b: number; night: number }

//                 t      r    g    b   night
const GRADE_STOPS: [number, number, number, number, number][] = [
  [0.0, 96, 108, 158, 0.9],   // deep night
  [0.06, 118, 126, 168, 0.7],
  [0.13, 255, 198, 152, 0.28], // warm dawn
  [0.22, 255, 246, 234, 0.05],
  [0.35, 255, 255, 255, 0],    // clear noon
  [0.6, 255, 252, 242, 0],
  [0.72, 255, 205, 145, 0.15], // orange dusk
  [0.8, 190, 168, 168, 0.45],
  [0.88, 112, 120, 165, 0.8],
  [1.0, 96, 108, 158, 0.9],
]

export function dayGrade(t: number): Grade {
  const x = ((t % 1) + 1) % 1
  for (let i = 1; i < GRADE_STOPS.length; i++) {
    const a = GRADE_STOPS[i - 1]
    const b = GRADE_STOPS[i]
    if (x <= b[0]) {
      const f = (x - a[0]) / (b[0] - a[0])
      return {
        r: lerp(a[1], b[1], f), g: lerp(a[2], b[2], f),
        b: lerp(a[3], b[3], f), night: lerp(a[4], b[4], f),
      }
    }
  }
  const l = GRADE_STOPS[GRADE_STOPS.length - 1]
  return { r: l[1], g: l[2], b: l[3], night: l[4] }
}

// ---- multi-hex footprints (the town hall) ----------------------------------------------

// A multi-hex building is a built platform: its whole footprint is LEVELLED to the
// anchor tile's height (foot[0]) so the plaza reads flat and the art sits centred on
// it, even where the underlying terrain slopes. Returns that single lift.
function footprintLift(st: GameState, foot: { q: number; r: number }[]): number {
  return elevLift(st, foot[0].q, foot[0].r)
}

function footprintCentroid(st: GameState, foot: { q: number; r: number }[]): { x: number; y: number } {
  let x = 0
  let y = 0
  for (const c of foot) {
    const p = hexToPixel(c.q, c.r, S)
    x += p.x
    y += p.y
  }
  return { x: x / foot.length, y: y / foot.length - footprintLift(st, foot) }
}

/** Lay a faction-tinted stone plaza across every hex a multi-tile building occupies.
 *  A town hall is a LEVEL platform (its footprint is required flat), so the whole plaza
 *  is drawn at the one anchor height — a single flat slab, not stepped per hex. That
 *  keeps a legacy hall whose ground still spans a tier step from showing vertical cliff
 *  borders between its plaza hexes; the art (also anchored at that height) sits flush. */
function drawFootprintBase(
  ctx: CanvasRenderingContext2D,
  foot: { q: number; r: number }[],
  color: string,
  st: GameState,
): void {
  const lift = footprintLift(st, foot)
  ctx.save()
  for (const c of foot) {
    const p = hexToPixel(c.q, c.r, S)
    ctx.beginPath()
    hexPathAt(ctx, p.x, p.y - lift, S * 0.98)
    ctx.fillStyle = 'rgba(58,52,44,0.55)'
    ctx.fill()
    ctx.lineWidth = 1.5
    ctx.strokeStyle = color
    ctx.globalAlpha = 0.5
    ctx.stroke()
    ctx.globalAlpha = 1
  }
  ctx.restore()
}

/** Even-odd clip = the tile (q,r)'s top MINUS any higher neighbour that occludes it,
 *  with the tile centred at the current translated origin (its lifted screen centre).
 *  A ground-draped building part (the farm field) is drawn in the building pass, after
 *  all terrain, so without this it paints over the taller hex sitting behind it. */
function clipVisibleTopRel(ctx: CanvasRenderingContext2D, st: GameState, q: number, r: number): void {
  const tp = hexToPixel(q, r, S)
  const hl = elevLift(st, q, r)
  ctx.beginPath()
  hexPathAt(ctx, 0, 0, S * 1.08)
  for (const d of DIRS) {
    const nq = q + d.q
    const nr = r + d.r
    const nl = elevLift(st, nq, nr)
    if (nl <= hl) continue
    const np = hexToPixel(nq, nr, S)
    hexPathAt(ctx, np.x - tp.x, np.y - nl - (tp.y - hl), S * 1.08)
  }
  ctx.clip('evenodd')
}

/** Clip a territory tile to the part that visually belongs to it, in absolute world
 *  coords. Subtracts two kinds of neighbour (like the 2.5D hover): TALLER ones (any
 *  direction) that occlude this tile, and LOWER ones BEHIND it (up-screen diagonals,
 *  d.r < 0) that this tile's lifted top is painted up over — otherwise the frontier
 *  line and wash spill onto the lower hex behind/below the tile. */
function clipVisibleTopAbs(ctx: CanvasRenderingContext2D, st: GameState, q: number, r: number): void {
  const p = hexToPixel(q, r, S)
  const hl = elevLift(st, q, r)
  ctx.beginPath()
  hexPathAt(ctx, p.x, p.y - hl, S * 1.08)
  for (const d of DIRS) {
    const nq = q + d.q
    const nr = r + d.r
    const nl = elevLift(st, nq, nr)
    if (!(nl > hl || (nl < hl && d.r < 0))) continue
    const np = hexToPixel(nq, nr, S)
    hexPathAt(ctx, np.x, np.y - nl, S * 1.08)
  }
  ctx.clip('evenodd')
}

// ---- buildMode ghost validity (client-side approximation) ------------------------------

export function ghostValid(st: GameState, type: BuildingType, q: number, r: number): boolean {
  const t = terrainAt(st, q, r)
  if (t === undefined) return false
  if (type === 'pier') {
    // The pier is valid exactly when the shoreline snap resolves to a legal
    // land-anchor + water-deck pair — the same resolution the ghost draws — and
    // neither hex sits in another faction's territory (the early return here would
    // otherwise skip the ownership check every other build runs, so an enemy shore
    // showed a green ghost the server rejects).
    const plan = resolvePier(st, q, r, st.pierRotate)
    if (plan === null || !fogVisible(fogOf(st), plan.q, plan.r)) return false
    for (const c of buildingFootprint('pier', plan.q, plan.r, plan.dir)) {
      const owner = ownerAt(st, c.q, c.r)
      if (owner !== undefined && owner !== st.me) return false
    }
    return true
  }
  if (type === 'ramp') {
    // A ramp is valid on buildable, unoccupied, in-sight ground that borders a
    // cliff it can bridge (rampDir mirrors the server's canPlace edge rule).
    if (!fogVisible(fogOf(st), q, r) || !TERRAIN[t].buildable) return false
    if (rampDir(st, q, r, st.pierRotate) === null) return false
    for (const b of st.buildings.values()) if (b.q === q && b.r === r) return false
    return true
  }
  // Fog of war: the server only allows building within sight (and would reject
  // it anyway) — show the red ghost so the rule is visible before the click.
  if (!fogVisible(fogOf(st), q, r)) return false
  const def = BUILDINGS[type]
  // Multi-hex footprints: every occupied cell must be clear. The farm's triangle
  // follows the pending R-rotation while placing.
  const cells = buildingFootprint(type, q, r, type === 'farm' ? st.pierRotate : undefined)
  if (type === 'chop') {
    // A chop targets a standing tree, not a terrain type.
    if (!treeAt(st, q, r)) return false
  } else if (type === 'plant') {
    if (!def.on?.includes(t) || treeAt(st, q, r) || roadAt(st, q, r)) return false
  } else if (type === 'road') {
    if (!isPassable(t) || roadAt(st, q, r)) return false
  } else if (def.on) {
    for (const c of cells) {
      const ct = terrainAt(st, c.q, c.r)
      if (ct === undefined || !def.on.includes(ct)) return false
    }
  } else {
    for (const c of cells) {
      const ct = terrainAt(st, c.q, c.r)
      if (ct === undefined || !TERRAIN[ct].buildable) return false
    }
  }
  // A standing tree no longer blocks a placement — the crew fells any trees on the
  // footprint before building/digging (server tickConstruction). So the ghost stays
  // green on a wooded hex; the click just queues a bit of felling first.
  // The town hall's crew levels the platform itself during site prep — placement only
  // demands a workable rim vs that levelled target (mirrors the server's relaxed rule).
  if (type === 'townhall' && townhallGroundReason(cells, (hq, hr) => elevAt(st, hq, hr), false)) return false
  // A hex at height `h` is reachable only if some neighbour sits within one tier —
  // the same ≤1 rule edgeBlocked enforces for every step (high country is no longer
  // a free climb), mirroring the server's escapableAt.
  const escapableAt = (h: number): boolean =>
    DIRS.some((d) => Math.abs((elevAt(st, q + d.q, r + d.r) ?? 0) - h) <= 1)
  // Digging: elevation worlds only, never below the waterline, never an
  // unclimbable pit (mirrors the server).
  if (type === 'dig') {
    const e = elevAt(st, q, r)
    if (e === undefined || e < 1) return false
    if (!escapableAt(e - 1)) return false
  }
  // Filling: elevation worlds only, capped height, never a strand-pillar.
  if (type === 'fill' || type === 'fillsand') {
    const e = elevAt(st, q, r)
    if (e === undefined || e >= 8) return false
    if (!escapableAt(e + 1)) return false
  }
  // Unlevel multi-hex plots are fine — the site crew regrades the footprint to its
  // majority tier during prep. Only a raise past the land's stacking limit is
  // impossible, so that one case still reads red (mirrors the server's canPlace).
  if (cells.length > 1) {
    const target = levelTarget(cells, (hq, hr) => elevAt(st, hq, hr))
    if (target !== undefined && target > FILL_MAX_TIER) {
      for (const c of cells) {
        const t = elevAt(st, c.q, c.r)
        if (t !== undefined && t < target) return false
      }
    }
  }
  if (def.adjacent) {
    let ok = false
    for (const n of neighbors(q, r)) {
      const nt = terrainAt(st, n.q, n.r)
      if (nt !== undefined && def.adjacent.includes(nt)) { ok = true; break }
    }
    if (!ok) return false
  }
  for (const b of st.buildings.values()) {
    for (const bc of buildingFootprint(b.type, b.q, b.r, b.dir)) {
      for (const c of cells) if (bc.q === c.q && bc.r === c.r) return false // land occupied
    }
  }
  // Land: build on any neutral (unowned) tile or your own — just not another
  // faction's land. Structures claim land; forestry orders do not.
  for (const c of cells) {
    const owner = ownerAt(st, c.q, c.r)
    if (owner !== undefined && owner !== st.me) return false
  }
  return true
}

/** Per-hex share of the placement rules, for footprint-cell highlighting: is THIS
 *  cell individually acceptable (visible, buildable ground, unoccupied, not another
 *  faction's land)? Townhall levelness is judged by townhallHexHint. Anchor-level rules
 *  (adjacency, pier/ramp snapping, road stacking) stay whole-placement — a failure
 *  there shows every cell red via ghostValid. */
export function ghostHexOk(
  st: GameState, type: BuildingType, _anchorQ: number, _anchorR: number, q: number, r: number,
): boolean {
  const t = terrainAt(st, q, r)
  if (t === undefined || !fogVisible(fogOf(st), q, r)) return false
  const def = BUILDINGS[type]
  if (type === 'chop') { if (!treeAt(st, q, r)) return false }
  else if (type === 'plant') { if (!def.on?.includes(t) || treeAt(st, q, r) || roadAt(st, q, r)) return false }
  else if (type === 'road') { if (!isPassable(t) || roadAt(st, q, r)) return false }
  else if (type === 'pier' || type === 'ramp') { /* snap-resolved; whole-placement */ }
  else if (def.on) { if (!def.on.includes(t)) return false }
  else if (!TERRAIN[t].buildable) return false
  // A standing tree no longer blocks a cell — the crew fells it first, then builds/digs.
  for (const b of st.buildings.values()) {
    for (const bc of buildingFootprint(b.type, b.q, b.r, b.dir)) {
      if (bc.q === q && bc.r === r) return false // occupied
    }
  }
  const owner = ownerAt(st, q, r)
  if (owner !== undefined && owner !== st.me) return false
  return true
}

// ---- effects ----------------------------------------------------------------------------

interface Ping { x: number; y: number; t0: number; color: string }

const PING_COLORS: Record<string, string> = {
  raid: '#ff5040', combat: '#ff8c3c', death: '#ff5040',
  build: '#7fd46a', diplo: '#6fa8ff', info: '#d8e0e6',
}

// ---- main loop -------------------------------------------------------------------------

export function startRenderer(canvas: HTMLCanvasElement, st: GameState, send: (m: ClientMsg) => void): void {
  void send // reserved; all commands flow through input/ui
  const ctx = canvas.getContext('2d')!
  initMinimap(st)

  let frame = 0
  let lastNow = performance.now()
  let lastTickAt = performance.now()
  st.on('tick', () => { lastTickAt = performance.now() })

  // -- gather popups: a "+N 🌾" that floats up from a producing building and
  // fades, so you can see your workers actually bringing goods in. Detected
  // entirely client-side from the building's buffer growing — no wire cost.
  // Fractional per-tick yield accumulates until a whole unit lands, then pops.
  // Weathering damage ('decay' events) rides the same float pipeline.
  interface Gather { x: number; y: number; t0: number; text: string }
  const gathers: Gather[] = []

  const pings: Ping[] = []
  st.on('events', (data: unknown) => {
    const list: GameEvent[] = Array.isArray(data) ? data : data ? [data as GameEvent] : []
    for (const ev of list) {
      if (ev.q === undefined || ev.r === undefined) continue
      const p = hexToPixel(ev.q, ev.r, S)
      if (ev.kind === 'decay') {
        gathers.push({ x: p.x, y: p.y - elevLift(st, ev.q, ev.r) - S, t0: performance.now(), text: ev.msg })
        if (gathers.length > 24) gathers.shift()
        continue
      }
      pings.push({ x: p.x, y: p.y - elevLift(st, ev.q, ev.r), t0: performance.now(), color: PING_COLORS[ev.kind] ?? '#d8e0e6' })
      if (pings.length > 40) pings.shift()
    }
  })
  const RES_LIST = Object.keys(RES_ICON) as Resource[]
  const bufSeen = new Map<string, number>() // "bid:res" -> last buffer value seen
  const bufAccum = new Map<string, number>() // "bid:res" -> unpopped fractional gain
  st.on('tick', () => {
    for (const b of st.buildings.values()) {
      if (b.owner !== st.me || !b.buffer) continue
      for (const res of RES_LIST) {
        const cur = b.buffer[res] ?? 0
        const key = b.id + ':' + res
        const prev = bufSeen.get(key)
        bufSeen.set(key, cur)
        if (prev === undefined || cur <= prev) continue // first sight, or hauled down
        let acc = (bufAccum.get(key) ?? 0) + (cur - prev)
        let whole = 0
        while (acc >= 1) {
          acc -= 1
          whole++
        }
        bufAccum.set(key, acc)
        if (whole === 0) continue
        const p = hexToPixel(b.q, b.r, S)
        gathers.push({
          x: p.x + (Math.random() - 0.5) * S * 0.6,
          y: p.y - S * 0.6 - elevLift(st, b.q, b.r),
          t0: performance.now(),
          text: `+${whole} ${RES_ICON[res]}`,
        })
        if (gathers.length > 60) gathers.shift()
      }
    }
  })

  // Smoothed unit render positions — published on the shared state so input
  // hit-tests against the exact pixels units are drawn at.
  const upos = st.unitPos

  // Gear-swap flourishes in flight, keyed by unit id. Captured at swap start (the
  // person still holds the old item then; the hall position is frozen in) and left to
  // play out its full duration even after the server resolves the swap a beat sooner.
  const swapAnim = new Map<
    number,
    { start: number; from: ItemType | null; to: ItemType | null; hx: number; hy: number }
  >()
  const SWAP_ANIM_MS = 1200

  function loop(now: number): void {
    frame++
    const dt = Math.min(0.1, (now - lastNow) / 1000)
    lastNow = now
    const dpr = window.devicePixelRatio || 1
    const { cw, ch } = viewSize(canvas)
    const bw = Math.max(1, Math.round(cw * dpr))
    const bh = Math.max(1, Math.round(ch * dpr))
    if (canvas.width !== bw || canvas.height !== bh) {
      canvas.width = bw
      canvas.height = bh
    }
    // Confine scrolling to the discovered map + 2x fog radius (no void safaris).
    clampCamera(st)
    // Ease the spin: rotF glides toward the rot target set by Q/E, so the view turns
    // smoothly instead of snapping. Snap-and-normalise once it lands so rot stays 0..5
    // and the rot-0 fast paths stay byte-identical.
    if (st.cam.rotF === undefined) st.cam.rotF = st.cam.rot ?? 0
    if (st.cam.rotF !== st.cam.rot) {
      st.cam.rotF += (st.cam.rot - st.cam.rotF) * Math.min(1, dt * 13)
      if (Math.abs(st.cam.rot - st.cam.rotF) < 0.0015) {
        const m = (((st.cam.rot % 6) + 6) % 6)
        st.cam.rot = m
        st.cam.rotF = m
      }
    }
    setViewRot(st) // continuous spin angle; wrapped hexToPixel (entities/overlays) read this
    const z = st.cam.zoom
    // Camera anchor in screen-aligned (rotated) space: tiles draw at rotated
    // positions, so the pan translate must use the camera's rotated position too.
    const camSA = viewRotate(st, st.cam.x, st.cam.y)
    const camX = camSA.x
    const camY = camSA.y
    const minX = camX - cw / (2 * z)
    const maxX = camX + cw / (2 * z)
    // The oblique tilt squashes world-y by GROUND_TILT, so the visible world-y
    // half-range is that much larger — widen the vertical cull bounds to match.
    const tilt = st.hasElev && st.lift25 ? GROUND_TILT : 1
    const minY = camY - ch / (2 * z * tilt)
    const maxY = camY + ch / (2 * z * tilt)

    const worldTransform = () => {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      ctx.translate(cw / 2, ch / 2)
      ctx.scale(z, z * tilt) // vertical foreshorten = the camera angle (2.5D only)
      ctx.translate(-camX, -camY)
    }
    // Same view, but with the Q/E spin applied as a real ctx.rotate around the
    // unrotated camera — for layers baked in FLAT world coords (the fog mask), which
    // otherwise wouldn't line up with the screen-aligned terrain. Identity at rot 0.
    const ang = camAngle(st)
    const worldTransformFlat = () => {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      ctx.translate(cw / 2, ch / 2)
      ctx.scale(z, z * tilt)
      if (ang) ctx.rotate(ang)
      ctx.translate(-st.cam.x, -st.cam.y)
    }
    // Publish the hex→screen projection for DOM overlays (tutorial spotlight):
    // the same transform the entity pass draws with — rotation-aware hexToPixel,
    // elevation lift, tilt foreshorten, camera pan and zoom.
    st.projectHex = (q, r) => {
      const p = hexToPixel(q, r, S)
      const x = (p.x - camX) * z + cw / 2
      const y = (p.y - elevLift(st, q, r) - camY) * z * tilt + ch / 2
      return { x, y, vis: x >= 0 && x <= cw && y >= 0 && y <= ch }
    }

    // Flat-world AABB of the visible region (inverse-rotate the screen box corners),
    // so the fog mask bakes over the tiles actually on screen when spun.
    let fMinX = minX, fMaxX = maxX, fMinY = minY, fMaxY = maxY
    if (ang) {
      fMinX = fMinY = Infinity; fMaxX = fMaxY = -Infinity
      const hx = cw / (2 * z), hy = ch / (2 * z * tilt)
      for (const ox of [-hx, hx]) for (const oy of [-hy, hy]) {
        const w = viewUnrotate(st, ox, oy)
        const wx = st.cam.x + w.x, wy = st.cam.y + w.y
        if (wx < fMinX) fMinX = wx; if (wx > fMaxX) fMaxX = wx
        if (wy < fMinY) fMinY = wy; if (wy > fMaxY) fMaxY = wy
      }
    }

    // -- background (void beyond loaded chunks)
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.fillStyle = '#0c1622'
    ctx.fillRect(0, 0, cw, ch)

    worldTransform()
    ctx.imageSmoothingEnabled = true

    // -- terrain chunk bitmaps. Bitmaps bake at the DISCRETE orientation (bmp.rot);
    // during a spin the view angle is between steps, so each bitmap is re-placed with a
    // per-chunk rotate of (viewAngle − bmp.rot·60°). Because the delta is per bitmap, a
    // half-rebaked frame (some chunks new orientation, some old) still composes cleanly
    // — no flash. At rest the delta is 0 and it's the plain axis-aligned blit as before.
    const keys = visibleChunkKeys(st, canvas)
    const scale = bucketScale(z * dpr)
    const budget = { n: 4 }
    const bitmaps: ReturnType<typeof getChunkBitmap>[] = []
    const visChunks: string[] = []
    const theta = camAngle(st)
    let rotatedBlit = false
    for (const key of keys) {
      const ck = st.chunks.get(key)
      if (!ck) continue
      visChunks.push(key)
      const bmp = getChunkBitmap(st, ck, scale, frame, budget)
      if (!bmp) continue
      const delta = theta - bmp.rot * HEX_TAU6
      if (delta === 0) {
        if (rotatedBlit) { worldTransform(); rotatedBlit = false }
        if (bmp.ox > maxX || bmp.ox + bmp.w < minX || bmp.oy > maxY || bmp.oy + bmp.h < minY) continue
        ctx.drawImage(bmp.canvas, bmp.ox, bmp.oy, bmp.w, bmp.h)
      } else {
        // Place this bitmap (baked at bmp.rot) at the continuous view angle.
        const camB = rotXY(st.cam.x, st.cam.y, bmp.rot * HEX_TAU6)
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
        ctx.translate(cw / 2, ch / 2)
        ctx.scale(z, z * tilt)
        ctx.rotate(delta)
        ctx.translate(-camB.x, -camB.y)
        rotatedBlit = true
        ctx.drawImage(bmp.canvas, bmp.ox, bmp.oy, bmp.w, bmp.h)
      }
      bitmaps.push(bmp)
    }
    if (rotatedBlit) worldTransform() // restore for the layers below
    pruneChunkCache(frame)
    setViewRot(st) // getChunkBitmap left the module angle at the discrete bake angle;
    // restore the continuous view angle for territory/entities/overlays drawn below

    // -- animated water sparkle. Skipped mid-spin: the sparkles are baked at the
    // discrete orientation and would drift from the rotating terrain for ~200ms.
    const spinning = (st.cam.rotF ?? 0) !== (st.cam.rot ?? 0)
    const shimmerCap = { n: 700 }
    if (!spinning) for (const bmp of bitmaps) if (bmp) drawWaterShimmer(ctx, bmp, now, z, minX, minY, maxX, maxY, shimmerCap)

    // -- territory wash + crisp borders (live, from chunk owners)
    drawTerritory(ctx, st, visChunks, z, minX, minY, maxX, maxY)

    // -- entities (viewport-culled, painter-sorted)
    const selUnits = new Set(st.selection.units)
    // What the cursor would select this frame — highlighted with an outline. Picked
    // from st.mouse every frame (not just on mousemove) so it tracks moving units and
    // camera pans; suppressed while placing a building. A panel chip pointing at a
    // building (st.uiHover) wins — the cursor's pick is stale under the panel.
    const hoverEnt = st.uiHover ?? (!st.buildMode && st.mouse ? pickEntity(st, canvas, st.mouse.x, st.mouse.y) : null)
    const pad = S * 2.5
    const vb: Building[] = []
    for (const b of st.buildings.values()) {
      const p = hexToPixel(b.q, b.r, S)
      if (p.x < minX - pad || p.x > maxX + pad || p.y < minY - pad || p.y > maxY + pad) continue
      vb.push(b)
    }
    // Back-to-front by lifted screen-y so raised buildings occlude lower ones.
    const bldKey = (bb: Building) => hexToPixel(bb.q, bb.r, S).y - elevLift(st, bb.q, bb.r)
    vb.sort((a, b) => bldKey(a) - bldKey(b) || a.q - b.q)
    for (const b of vb) {
      const foot = buildingFootprint(b.type, b.q, b.r, b.dir)
      // Multi-hex buildings (the town hall) center their art on the footprint centroid
      // and lay a stone plaza across every occupied hex.
      const multi = foot.length > 1
      const p = multi ? footprintCentroid(st, foot) : hexToPixel(b.q, b.r, S)
      if (!multi) p.y -= elevLift(st, b.q, b.r) // single-hex: ride its tile (centroid is already lifted)
      const color = st.factions.get(b.owner)?.color ?? '#b0a48c'
      if (multi && b.type !== 'farm') drawFootprintBase(ctx, foot, color, st)
      ctx.save()
      ctx.translate(p.x, p.y)
      if (st.selection.building === b.id) {
        drawSelectionRing(ctx, S * 0.85, S * 0.5, color)
        // Defensive buildings show their attack reach: a faint covered-area
        // wash plus a bright dashed rim, so the radius reads at a glance.
        const atk = BUILDINGS[b.type].attack
        if (atk && b.done) {
          const rad = (atk.range + 0.5) * S * SQRT3
          const wash = ctx.createRadialGradient(0, 0, rad * 0.55, 0, 0, rad)
          wash.addColorStop(0, 'rgba(255, 130, 95, 0.02)')
          wash.addColorStop(1, 'rgba(255, 130, 95, 0.12)')
          ctx.fillStyle = wash
          ctx.beginPath()
          ctx.arc(0, 0, rad, 0, TAU)
          ctx.fill()
          ctx.strokeStyle = 'rgba(255, 145, 105, 0.9)'
          ctx.lineWidth = 2.2 / z
          ctx.setLineDash([7 / z, 5 / z])
          ctx.beginPath()
          ctx.arc(0, 0, rad, 0, TAU)
          ctx.stroke()
          ctx.setLineDash([])
        }
      }
      // The town hall is a capital spanning a 7-hex plaza — draw its art oversized
      // so it spills a little onto every surrounding hex instead of sitting on the
      // centre one. Only the art scales; the HP/overlay stays true-size.
      // Art is drawn upright (scale 1,1/tilt below); pass the tilt back so ground-draped
      // parts (the farm field) press flat onto the tile instead of standing up.
      // A site still in preparation draws as a see-through planned-building overlay
      // (land untouched underneath) — recomputed live so it flips to the blueprint
      // the moment the crew finishes clearing and levelling. (Roads have no art to
      // preview; drawSite marks their clearing with survey stakes instead.)
      const prep = !b.done && !isBoxOrder(b.type) && sitePrepPhase(st, b) !== null
      const drawArt = (c: CanvasRenderingContext2D): void => {
        if (b.type === 'townhall') {
          c.save()
          c.scale(TOWNHALL_ART_SCALE, TOWNHALL_ART_SCALE)
          drawBuilding(c, b, color, now, tilt, prep, st.tick)
          c.restore()
        } else {
          drawBuilding(c, b, color, now, tilt, prep, st.tick)
        }
      }
      // The farm drapes a tilled field on each of its 3 footprint hexes (each
      // clipped to its own tile top); the barn (drawArt below) stands upright at
      // the footprint centroid, amid the fields.
      if (b.type === 'farm' && b.done) {
        // foot already honors b.dir (computed above with the building's dir)
        for (let ci = 0; ci < foot.length; ci++) {
          const c = foot[ci]
          const cp = hexToPixel(c.q, c.r, S)
          ctx.save()
          ctx.translate(cp.x - p.x, cp.y - elevLift(st, c.q, c.r) - p.y)
          clipVisibleTopRel(ctx, st, c.q, c.r)
          ctx.scale(1, 1 / tilt)
          drawFarmField(ctx, b.id * 31 + ci, tilt)
          ctx.restore()
        }
      }
      ctx.save()
      ctx.scale(1, 1 / tilt) // draw the building upright on the tilted ground
      if (hoverEnt?.kind === 'building' && hoverEnt.id === b.id) drawHoverSilhouette(ctx, z, drawArt)
      drawArt(ctx)
      drawBuildingOverlay(ctx, b, now)
      ctx.restore()
      ctx.restore()
      if (st.selection.building === b.id && b.rally) {
        const rp = hexToPixel(b.rally.q, b.rally.r, S)
        rp.y -= elevLift(st, b.rally.q, b.rally.r)
        ctx.save()
        ctx.strokeStyle = color
        ctx.globalAlpha = 0.5
        ctx.setLineDash([S * 0.2, S * 0.2])
        ctx.lineWidth = 1.4
        ctx.beginPath()
        ctx.moveTo(p.x, p.y)
        ctx.lineTo(rp.x, rp.y)
        ctx.stroke()
        ctx.setLineDash([])
        ctx.globalAlpha = 1
        ctx.translate(rp.x, rp.y)
        drawRallyFlag(ctx, color, now)
        ctx.restore()
      }
    }

    // Assign each unit sharing a hex a distinct slot (centre + 6 corners) so a
    // crowded hex fans out instead of stacking — shared with input hit-testing.
    const slotOf = assignUnitSlots(st.units.values())

    // units: interpolate fq/fr→q/r by mp, offset into slot, then glide between
    // successive server states at constant velocity (one tick of render delay —
    // fluid motion instead of chase-and-stall between ticks)
    const vu: { u: UnitWire; x: number; y: number; tx: number; ty: number }[] = []
    for (const u of st.units.values()) {
      const a = hexToPixel(u.fq, u.fr, S)
      const b = hexToPixel(u.q, u.r, S)
      const mp = Math.max(0, Math.min(1, u.mp))
      const slot = slotOf.get(u.id)
      const ox = slot ? slot.dx * S : 0
      const oy = slot ? slot.dy * S : 0
      const tx = lerp(a.x, b.x, mp) + ox
      // 2.5D: ride the tile height, interpolated across the hop so a unit climbs a
      // ramp smoothly. Baked into sp.y (below), so picking (which reads unitPos)
      // stays consistent with the draw.
      const liftY = lerp(elevLift(st, u.fq, u.fr), elevLift(st, u.q, u.r), mp)
      const ty = lerp(a.y, b.y, mp) + oy - liftY
      let sp = upos.get(u.id)
      if (!sp) {
        sp = { x: tx, y: ty, ax: tx, ay: ty, bx: tx, by: ty, t0: now }
        upos.set(u.id, sp)
      } else {
        sp.bx ??= sp.x
        sp.by ??= sp.y
        sp.ax ??= sp.x
        sp.ay ??= sp.y
        sp.t0 ??= now
        if (Math.hypot(tx - sp.bx, ty - sp.by) > 0.01) {
          // fresh server target: the old target becomes the glide's start
          if (Math.hypot(tx - sp.bx, ty - sp.by) > S * 6) {
            sp.ax = tx // teleport (respawn/first sight): snap everything
            sp.ay = ty
          } else {
            sp.ax = sp.bx
            sp.ay = sp.by
          }
          sp.bx = tx
          sp.by = ty
          sp.t0 = now
        }
        const k = Math.min(1, (now - sp.t0) / TICK_MS)
        sp.x = lerp(sp.ax, sp.bx, k)
        sp.y = lerp(sp.ay, sp.by, k)
      }
      if (sp.x < minX - pad || sp.x > maxX + pad || sp.y < minY - pad || sp.y > maxY + pad) continue
      vu.push({ u, x: sp.x, y: sp.y, tx, ty })
    }
    vu.sort((a, b) => a.y - b.y)
    // Route lines for selected own units: from their feet along the remaining
    // hops to the destination, with a small end dot. Drawn under the units.
    for (const id of selUnits) {
      const u = st.units.get(id)
      const sp = upos.get(id)
      if (!u || !sp || u.owner !== st.me) continue
      const wp = u.wpath
      if (!wp || wp.length === 0) continue
      // Trim hops already walked: start from the hop nearest our current hex.
      let start = 0
      for (let i = 0; i < Math.min(3, wp.length); i++) {
        if (wp[i].q === u.q && wp[i].r === u.r) start = i + 1
      }
      if (start >= wp.length) continue
      ctx.save()
      ctx.strokeStyle = 'rgba(240, 236, 214, 0.55)'
      ctx.lineWidth = 1.6 / z
      ctx.setLineDash([5 / z, 4 / z])
      ctx.beginPath()
      ctx.moveTo(sp.x, sp.y)
      for (let i = start; i < wp.length; i++) {
        const hp2 = hexToPixel(wp[i].q, wp[i].r, S)
        ctx.lineTo(hp2.x, hp2.y - elevLift(st, wp[i].q, wp[i].r))
      }
      ctx.stroke()
      ctx.setLineDash([])
      const endW = wp[wp.length - 1]
      const end = hexToPixel(endW.q, endW.r, S)
      ctx.fillStyle = 'rgba(240, 236, 214, 0.8)'
      ctx.beginPath()
      ctx.arc(end.x, end.y - elevLift(st, endW.q, endW.r), S * 0.14, 0, TAU)
      ctx.fill()
      ctx.restore()
    }
    const toolWarnings: { tool: ItemType; x: number; y: number }[] = []
    for (const { u, x, y, tx, ty } of vu) {
      const color = st.factions.get(u.owner)?.color ?? (u.owner === 'wild' ? '#8a2f2f' : '#b0a48c')
      // Bobbing while still gliding toward its (slotted) target, or mid-hop.
      const walking = Math.hypot(tx - x, ty - y) > 0.8 || (u.mp > 0 && u.mp < 1)
      const bob = walking ? Math.abs(Math.sin(now * 0.012 + u.id * 2.1)) * -S * 0.07 : 0
      ctx.save()
      ctx.translate(x, y)
      if (selUnits.has(u.id)) drawSelectionRing(ctx, S * 0.34, S * 0.2, color)
      ctx.scale(1, 1 / tilt) // upright serf on the tilted ground
      if (hoverEnt?.kind === 'unit' && hoverEnt.id === u.id) {
        drawHoverSilhouette(ctx, z, (c) => drawUnit(c, u, color, now, bob))
      }
      drawUnit(ctx, u, color, now, bob)
      // Status glyph overhead: what this serf is doing right now. The hovered
      // unit skips it (the tile/name affordances own that spot).
      if (!(hoverEnt?.kind === 'unit' && hoverEnt.id === u.id)) {
        const tool = missingWorkTool(st, u)
        const icon = statusIcon(st, u)
        if (tool) {
          // Paint screen-sized warnings after site badges so a construction
          // marker cannot cover the worker's missing-tool symbol.
          const lift = -S * 1.15 + bob - 14 / z, matrix = ctx.getTransform()
          toolWarnings.push({ tool, x: (matrix.c * lift + matrix.e) / dpr, y: (matrix.d * lift + matrix.f) / dpr })
        } else if (icon) {
          drawStatusBadge(ctx, icon, u, 0, -S * 1.15 + bob, S * 0.5)
        }
      }
      ctx.restore()
    }
    if (frame % 300 === 0) {
      for (const id of upos.keys()) if (!st.units.has(id)) upos.delete(id)
    }

    // -- event pings: expanding fading rings
    for (let i = pings.length - 1; i >= 0; i--) {
      const p = pings[i]
      const age = (now - p.t0) / 1400
      if (age >= 1) { pings.splice(i, 1); continue }
      if (p.x < minX - S * 4 || p.x > maxX + S * 4 || p.y < minY - S * 4 || p.y > maxY + S * 4) continue
      ctx.strokeStyle = p.color
      ctx.globalAlpha = (1 - age) * 0.9
      ctx.lineWidth = 2.2 / z
      ctx.beginPath()
      ctx.arc(p.x, p.y, S * (0.4 + age * 3.2), 0, TAU)
      ctx.stroke()
      ctx.globalAlpha = 1
    }

    // -- gather popups: rise and fade above the building that produced them
    if (gathers.length > 0) {
      ctx.save()
      const gfs = Math.max(11 / z, 3.5)
      ctx.font = `700 ${gfs}px system-ui, sans-serif`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'bottom'
      ctx.lineWidth = Math.max(3 / z, 1)
      for (let i = gathers.length - 1; i >= 0; i--) {
        const g = gathers[i]
        const age = (now - g.t0) / 1300
        if (age >= 1) {
          gathers.splice(i, 1)
          continue
        }
        const gy = g.y - age * S * 1.6 // float upward
        if (g.x < minX - S * 4 || g.x > maxX + S * 4 || gy < minY - S * 4 || gy > maxY + S * 4) continue
        ctx.globalAlpha = age < 0.12 ? age / 0.12 : 1 - (age - 0.12) / 0.88 // pop in, ease out
        ctx.save()
        ctx.translate(g.x, gy)
        ctx.scale(1, 1 / tilt) // keep the "+N 🌾" upright on the tilt
        ctx.strokeStyle = 'rgba(10,12,10,0.85)'
        ctx.strokeText(g.text, 0, 0)
        ctx.fillStyle = '#d6f0b8'
        ctx.fillText(g.text, 0, 0)
        ctx.restore()
      }
      ctx.globalAlpha = 1
      ctx.restore()
    }

    // -- guard rings: selected/hovered armed serfs show their watch radius
    {
      const ringIds = new Set<number>(selUnits)
      if (hoverEnt?.kind === 'unit') ringIds.add(hoverEnt.id)
      for (const id of ringIds) {
        const gu = st.units.get(id)
        const gp = upos.get(id)
        if (!gu || !gp || gu.owner !== st.me || gu.type !== 'citizen') continue
        const gh = gu.person?.held
        if (gh !== 'spear' && gh !== 'sword' && gh !== 'bow') continue
        ctx.save()
        ctx.strokeStyle = 'rgba(150, 190, 255, 0.55)'
        ctx.lineWidth = 1.6 / z
        ctx.setLineDash([6 / z, 5 / z])
        ctx.beginPath()
        ctx.arc(gp.x, gp.y, (GUARD_RANGE + 0.5) * S * SQRT3, 0, TAU)
        ctx.stroke()
        ctx.setLineDash([])
        ctx.restore()
      }
    }

    // -- hovered unit: a floating "who & what" tag above their head
    if (hoverEnt?.kind === 'unit') {
      const hu = st.units.get(hoverEnt.id)
      const hp2 = upos.get(hoverEnt.id)
      if (hu && hp2) {
        const nm = hu.person?.name ?? (hu.type.charAt(0).toUpperCase() + hu.type.slice(1))
        const sh = hu.person ? starvingHours(st, hu.person) : null
        const tool = missingWorkTool(st, hu)
        const label =
          (hu.owner === st.me ? `${nm} — ${unitActivity(st, hu)}` : nm) +
          (tool ? ` · Needs ${ITEMS[tool].name.toLowerCase()}` : '') +
          (sh !== null ? ` · STARVING ${sh.toFixed(1)}h` : '')
        ctx.save()
        const fs = Math.max(10.5 / z, 3.5)
        ctx.font = `600 ${fs}px system-ui, sans-serif`
        ctx.textAlign = 'center'
        ctx.textBaseline = 'bottom'
        ctx.translate(hp2.x, hp2.y - S * 1.05)
        ctx.scale(1, 1 / tilt) // undo the ground squash so the tag stays readable
        ctx.lineWidth = Math.max(3 / z, 1)
        ctx.strokeStyle = 'rgba(10,12,10,0.85)'
        ctx.strokeText(label, 0, 0)
        ctx.fillStyle = tool ? '#f4c566' : '#efe9d8'
        ctx.fillText(label, 0, 0)
        ctx.restore()
      }
    }

    // -- fog of war: out-of-sight land dims to explored memory (still in world space).
    // The mask is baked in flat world coords, so composite it under the spin.
    worldTransformFlat()
    drawFog(ctx, st, fMinX, fMinY, fMaxX, fMaxY)

    // -- day/night grade
    const tickF = st.tick + Math.min(1, (now - lastTickAt) / 1000)
    const g = dayGrade((tickF % DAY_TICKS) / DAY_TICKS)
    if (g.r < 254 || g.g < 254 || g.b < 254) {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      ctx.globalCompositeOperation = 'multiply'
      ctx.fillStyle = `rgb(${g.r | 0},${g.g | 0},${g.b | 0})`
      ctx.fillRect(0, 0, cw, ch)
      ctx.globalCompositeOperation = 'source-over'
    }
    // warm additive glow around finished buildings + camp fires at night
    if (g.night > 0.04) {
      worldTransform()
      ctx.globalCompositeOperation = 'lighter'
      let glows = 0
      for (const b of vb) {
        if (glows > 90) break
        if (!b.done) continue
        const p = hexToPixel(b.q, b.r, S)
        const py = p.y - elevLift(st, b.q, b.r) // glow rides the lit building
        let rad = S * 1.5
        let a = 0.26 * g.night
        if (b.type === 'townhall') { rad = S * 2.6; a = 0.34 * g.night }
        else if (b.type === 'camp') {
          rad = S * 2.0
          a = (0.3 + 0.1 * Math.sin(now * 0.011 + b.id)) * g.night
        } else if (b.type === 'road') continue
        const gr = ctx.createRadialGradient(p.x, py, 0, p.x, py, rad)
        gr.addColorStop(0, `rgba(255,180,95,${a})`)
        gr.addColorStop(1, 'rgba(255,180,95,0)')
        ctx.fillStyle = gr
        ctx.beginPath()
        ctx.arc(p.x, py, rad, 0, TAU)
        ctx.fill()
        glows++
      }
      ctx.globalCompositeOperation = 'source-over'
    }

    // -- gear-swap flourishes at the armory (crisp, above the grade)
    worldTransform()
    for (const [id, a] of swapAnim) {
      const sp = upos.get(id)
      const p = (now - a.start) / SWAP_ANIM_MS
      if (!sp || p >= 1 || !st.units.has(id)) { swapAnim.delete(id); continue }
      drawGearSwap(ctx, a.from, a.to, sp.x, sp.y, a.hx, a.hy, p)
    }
    for (const u of st.units.values()) {
      if (u.state === 'errand' && u.errand?.swap != null && !swapAnim.has(u.id)) {
        const hallB = u.errand.hall !== undefined ? st.buildings.get(u.errand.hall) : undefined
        if (hallB) {
          const hp = hexToPixel(hallB.q, hallB.r, S)
          swapAnim.set(u.id, { start: now, from: u.person?.held ?? null, to: u.errand.to, hx: hp.x, hy: hp.y - S * 0.5 - elevLift(st, hallB.q, hallB.r) })
        }
      }
    }

    // Drag-paint preview: light up every hex the in-progress stroke covers — green
    // where the order (road / dig / fill) can go, red where it can't, from each hex's
    // cached verdict (no per-frame revalidation). Committed on release; a back-drag
    // trims the tail. (ctx is in world space here — same transform the ghost below uses.)
    const painting = st.paintType !== null && st.paintPath.length > 0
    // A box order's Remove sub-mode tints its box amber (unqueueing) instead of green/red.
    // chopCancel is only ever set while a box-order tool is armed.
    const chopRemove = st.chopCancel
    if (painting) {
      for (const c of st.paintPath) {
        const cp = hexToPixel(c.q, c.r, S)
        ctx.beginPath()
        hexPathAt(ctx, cp.x, cp.y - elevLift(st, c.q, c.r), S * 0.9)
        ctx.fillStyle = chopRemove ? 'rgba(240, 160, 32, 0.36)' : c.ok ? 'rgba(95, 212, 106, 0.34)' : 'rgba(224, 80, 58, 0.36)'
        ctx.fill()
        ctx.lineWidth = 1.6 / z
        ctx.strokeStyle = chopRemove ? 'rgba(248, 180, 60, 0.95)' : c.ok ? 'rgba(120, 235, 130, 0.9)' : 'rgba(255, 90, 70, 0.9)'
        ctx.stroke()
      }
    }

    // The single-hover build ghost is suppressed while a paint stroke is active (the
    // stroke highlight already covers the cursor hex) — matching the 3D view.
    if (st.hover && !painting) {
      const hp = hexToPixel(st.hover.q, st.hover.r, S)
      hp.y -= elevLift(st, st.hover.q, st.hover.r) // ghost rides the hovered tile
      if (st.buildMode) {
        // The pier previews its SNAPPED placement (resolvePier mirrors the server's
        // shoreline snap plus the R-rotation): hovering water shows the anchor on
        // the adjacent land, hovering shore shows the deck on the chosen water side.
        // One cell is always buildable land and the other shallow water — a hover
        // that resolves to no legal pair renders a single red hex, never a green
        // pair of water hexes.
        const plan = st.buildMode === 'pier' ? resolvePier(st, st.hover.q, st.hover.r, st.pierRotate) : null
        const rampD = st.buildMode === 'ramp' ? rampDir(st, st.hover.q, st.hover.r, st.pierRotate) : null
        const aq = plan?.q ?? st.hover.q // placement anchor (≠ hover for a snapped pier)
        const ar = plan?.r ?? st.hover.r
        const valid =
          st.buildMode === 'pier'
            ? plan !== null && fogVisible(fogOf(st), aq, ar)
            : ghostValid(st, st.buildMode, st.hover.q, st.hover.r)
        const color = st.factions.get(st.me ?? '')?.color ?? '#e6e2d8'
        const foot =
          st.buildMode === 'pier' && !plan
            ? [{ q: st.hover.q, r: st.hover.r }]
            : buildingFootprint(st.buildMode, aq, ar, st.buildMode === 'farm' ? st.pierRotate : plan?.dir)
        const cells = foot.map((c) => {
          const cp = hexToPixel(c.q, c.r, S)
          return { x: cp.x - hp.x, y: cp.y - elevLift(st, c.q, c.r) - hp.y }
        })
        ctx.save()
        ctx.translate(hp.x, hp.y)
        const orient = plan ? plan.dir : rampD // oriented facing for pier/ramp, else undefined
        // Town hall: colour each footprint hex by the majority-height vote — the
        // platform the hall would level to reads green, the minority outliers red —
        // so the player sees which few hexes to move off, not a uniform red ghost.
        // Town hall only: its rim rule is the one remaining ground blocker (footprint
        // outliers are levelled by the site crew now, so cells read green).
        const hint = st.buildMode === 'townhall' ? townhallHexHint(foot, (hq, hr) => elevAt(st, hq, hr)) : null
        // A footprint cell is OK only if it wins the height vote AND is buildable terrain —
        // forest/mountain/water can't hold the hall, so they read red like height outliers
        // instead of green just because the ground is level (drawGhost's per-cell colour
        // otherwise ignores the overall `valid` flag).
        const cellOk = hint
          ? foot.map((c, i) => {
              const ct = terrainAt(st, c.q, c.r)
              return hint.cellOk[i] && ct !== undefined && TERRAIN[ct].buildable
            })
          : undefined
        drawGhost(ctx, st.buildMode, color, valid, now, cells, orient != null ? pierArtAngle(orient) : 0, cellOk)
        // Ring hexes too steep for that platform are blockers too — flag them red
        // just outside the footprint. Town-hall only: a farm has no rim rule, so a
        // steep neighbour (a cliff or a dug pit) must not paint a phantom red ring.
        if (hint && st.buildMode === 'townhall') {
          for (const c of hint.steepRing) {
            const cp = hexToPixel(c.q, c.r, S)
            const bx = cp.x - hp.x
            const by = cp.y - elevLift(st, c.q, c.r) - hp.y
            ctx.beginPath()
            hexPathAt(ctx, bx, by, S * 0.9)
            ctx.fillStyle = 'rgba(228, 58, 42, 0.28)'
            ctx.fill()
            ctx.lineWidth = 2.4 / z
            ctx.strokeStyle = 'rgba(255, 86, 66, 0.95)'
            ctx.stroke()
          }
        }
        // Facing arrow for oriented builds: pier anchor → water deck, or ramp
        // hex → the cliff it climbs, so the picked edge reads at a glance while R
        // (or the ⟳ chip) cycles it.
        const facing = plan ? { q: plan.q, r: plan.r, dir: plan.dir } : rampD != null ? { q: st.hover.q, r: st.hover.r, dir: rampD } : null
        if (facing && valid) {
          const ap = hexToPixel(facing.q, facing.r, S)
          const d = DIRS[facing.dir]
          const dp = hexToPixel(facing.q + d.q, facing.r + d.r, S)
          const x0 = ap.x - hp.x
          const y0 = ap.y - elevLift(st, facing.q, facing.r) - hp.y
          const x1 = dp.x - hp.x
          const y1 = dp.y - elevLift(st, facing.q + d.q, facing.r + d.r) - hp.y
          const ang = Math.atan2(y1 - y0, x1 - x0)
          const ex = lerp(x0, x1, 0.82)
          const ey = lerp(y0, y1, 0.82)
          ctx.strokeStyle = 'rgba(255, 252, 235, 0.95)'
          ctx.lineWidth = 2
          ctx.beginPath()
          ctx.moveTo(lerp(x0, x1, 0.3), lerp(y0, y1, 0.3))
          ctx.lineTo(ex, ey)
          ctx.moveTo(ex, ey)
          ctx.lineTo(ex + Math.cos(ang + 2.7) * S * 0.24, ey + Math.sin(ang + 2.7) * S * 0.24)
          ctx.moveTo(ex, ey)
          ctx.lineTo(ex + Math.cos(ang - 2.7) * S * 0.24, ey + Math.sin(ang - 2.7) * S * 0.24)
          ctx.stroke()
        }
        // Preview the land this building will claim when finished (only tiles
        // that are currently unowned actually join the realm).
        if (valid) {
          ctx.strokeStyle = 'rgba(140, 220, 140, 0.35)'
          ctx.lineWidth = 1.2 / z
          for (const { q: cq, r: cr } of buildingClaimTiles({
            type: st.buildMode, q: aq, r: ar, dir: st.buildMode === 'farm' ? st.pierRotate : plan?.dir,
          })) {
            if (ownerAt(st, cq, cr) !== undefined) continue // already claimed land keeps its owner
            const cp = hexToPixel(cq, cr, S)
            ctx.beginPath()
            hexPathAt(ctx, cp.x - hp.x, cp.y - elevLift(st, cq, cr) - hp.y, S * 0.92)
            ctx.stroke()
          }
        }
        // Placing a defensive building previews its attack reach.
        const atkDef = BUILDINGS[st.buildMode].attack
        if (atkDef && valid) {
          ctx.strokeStyle = 'rgba(255, 145, 105, 0.85)'
          ctx.lineWidth = 2.2 / z
          ctx.setLineDash([7 / z, 5 / z])
          ctx.beginPath()
          ctx.arc(0, 0, (atkDef.range + 0.5) * S * SQRT3, 0, TAU)
          ctx.stroke()
          ctx.setLineDash([])
        }
        ctx.restore()
        // Anchor placement details to the whole footprint (a pier spans two
        // hexes), so the card clears the ghost art instead of overlapping it.
        let footCx = hp.x
        let footTop = hp.y
        let footBot = hp.y
        if (foot.length > 1) {
          footCx = 0
          footTop = Infinity
          footBot = -Infinity
          for (const c of foot) {
            const cp = hexToPixel(c.q, c.r, S)
            const cy = cp.y - elevLift(st, c.q, c.r)
            footCx += cp.x
            footTop = Math.min(footTop, cy)
            footBot = Math.max(footBot, cy)
          }
          footCx /= foot.length
        }
        if (valid) {
          const card = placementPreview(st, st.buildMode, aq, ar, st.buildMode === 'farm' ? st.pierRotate : orient ?? undefined)
          if (card) {
            // Keep the card upright and a constant screen size at every camera
            // zoom and tilt. The current transform includes device pixel ratio.
            const matrix = ctx.getTransform()
            const ay = footTop - S * 1.35
            const sx = (matrix.a * footCx + matrix.c * ay + matrix.e) / dpr
            const sy = (matrix.b * footCx + matrix.d * ay + matrix.f) / dpr
            ctx.save()
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
            drawBuildPreviewCard(ctx, card, sx, sy, cw, ch)
            ctx.restore()
          }
        }
        // Pier placement hint under the ghost: rotation exists whenever the
        // anchor touches more than one water hex.
        if (plan && valid && plan.choices > 1) {
          ctx.save()
          const fs = Math.max(10 / z, 4)
          ctx.font = `600 ${fs}px system-ui, sans-serif`
          ctx.textAlign = 'center'
          ctx.textBaseline = 'top'
          ctx.translate(footCx, footBot + S * 1.1)
          ctx.scale(1, 1 / tilt)
          ctx.lineWidth = Math.max(3 / z, 1)
          ctx.strokeStyle = 'rgba(10,12,10,0.85)'
          ctx.strokeText('R to rotate', 0, 0)
          ctx.fillStyle = '#e8e4d8'
          ctx.fillText('R to rotate', 0, 0)
          ctx.restore()
        }
      } else if (!hoverEnt) {
        // Bare ground: outline the hovered tile. Suppressed when a unit/building is
        // hovered so its silhouette is the only highlight (no doubled markers).
        const lifted = st.hasElev && st.lift25
        ctx.save()
        if (lifted) {
          // Tint exactly the tile's visible top — its S*1.08 hexagon (the radius terrain
          // paints tops at) MINUS any TALLER neighbour that occludes it. This is the same
          // clip terrain uses, so the tint lands precisely on the tile as drawn: a clean
          // full-top highlight, never a truncated sliver.
          ctx.beginPath()
          hexPathAt(ctx, hp.x, hp.y, S * 1.08)
          const hl = elevLift(st, st.hover.q, st.hover.r)
          for (const d of DIRS) {
            const nq = st.hover.q + d.q
            const nr = st.hover.r + d.r
            const nl = elevLift(st, nq, nr)
            if (nl <= hl) continue
            const np = hexToPixel(nq, nr, S)
            hexPathAt(ctx, np.x, np.y - nl, S * 1.08)
          }
          ctx.clip('evenodd')
          ctx.fillStyle = 'rgba(240,244,240,0.16)'
          ctx.beginPath()
          hexPathAt(ctx, hp.x, hp.y, S * 1.08)
          ctx.fill()
        } else {
          // Flat top-down: a crisp hex outline is unambiguous and can't overhang.
          ctx.strokeStyle = 'rgba(240,244,240,0.75)'
          ctx.lineWidth = 1.5 / z
          ctx.beginPath()
          hexPathAt(ctx, hp.x, hp.y, S * 0.96)
          ctx.stroke()
        }
        ctx.restore()
      }
    }

    // Box-order badges: a status icon floating above every queued order — 🪓 to fell a
    // tree, 🕳️/⏫/🏖️ over terraform hexes (screen space, so it stays a constant size and
    // upright under the Q/E spin).
    {
      let anyOrder = false
      for (const b of st.buildings.values()) {
        if (b.owner !== st.me) continue
        // Box orders show their kind; a building SITE shows the stage it needs.
        let icon: string | null = null
        let chop = false
        if (isBoxOrder(b.type)) { icon = BUILD_ICON[b.type]; chop = b.type === 'chop' }
        else if (!b.done) icon = siteStageIcon(st, b)
        if (!icon) continue
        if (!anyOrder) {
          ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
          ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
          ctx.font = '12px system-ui, sans-serif'
          anyOrder = true
        }
        const wp = viewRotate(st, hexToPixel(b.q, b.r, S).x, hexToPixel(b.q, b.r, S).y)
        // Float clear of the standing tree for a chop / rising blueprint; sit lower over flat ground.
        const above = b.type === 'plant' ? Math.max(12, (b.done ? [0.75, 1.2, 1.7][treeGrowthStage(b, st.tick)] : 0.5) * S * z)
          : chop || !isBoxOrder(b.type) ? Math.max(18, S * 1.2 * z * tilt) : Math.max(12, S * 0.5 * z * tilt)
        const sx = cw / 2 + (wp.x - camX) * z
        const sy = ch / 2 + (wp.y - camY - elevLift(st, b.q, b.r)) * z * tilt - above
        if (sx < -20 || sx > cw + 20 || sy < -20 || sy > ch + 20) continue
        if (b.type === 'plant') {
          drawTreeGrowthBadge(ctx, b, st.tick, sx, sy, 10)
          continue
        }
        const rr = 10
        ctx.fillStyle = 'rgba(12,18,10,0.82)'
        ctx.beginPath(); ctx.arc(sx, sy, rr, 0, Math.PI * 2); ctx.fill()
        ctx.strokeStyle = '#f0a020'; ctx.lineWidth = 1.5
        ctx.beginPath(); ctx.arc(sx, sy, rr, 0, Math.PI * 2); ctx.stroke()
        ctx.fillText(icon, sx, sy + 1)
      }
      if (anyOrder) { ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic' }
    }

    // Storage warnings stay crisp above the world and its units. Ownership is
    // checked before reading contents, matching the private storage hover popup.
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    for (const warning of toolWarnings) drawToolWarning(ctx, warning.tool, warning.x, warning.y)
    for (const b of vb) {
      if (!b.done || b.owner !== st.me || (b.type !== 'townhall' && b.type !== 'storehouse')) continue
      const status = storageStatus(b)
      if (status.level === 'normal') continue
      const p = st.projectHex(b.q, b.r)
      // Art remains upright on tilted terrain, so its height scales by zoom only.
      let roofHeight = b.type === 'townhall'
        ? S * TOWNHALL_ART_SCALE * TOWNHALL_WARNING_HEIGHT[townHallPhase(b.hallPhase) - 1]
        : S * 0.52
      if (b.hp < buildingMaxHp(b) - 0.5) roofHeight = Math.max(roofHeight, S * 1.12)
      const y = p.y - roofHeight * z - 18
      if (p.x < -60 || p.x > cw + 60 || y < -20 || y > ch + 20) continue
      drawStorageWarning(ctx, p.x, y, status)
    }

    drawDiscoveries(ctx, st, cw, ch)

    // selection box (screen space; input maintains st.selBox when box-dragging)
    const sb = (st as unknown as { selBox?: { x0: number; y0: number; x1: number; y1: number } | null }).selBox
    if (sb) {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      const x = Math.min(sb.x0, sb.x1)
      const y = Math.min(sb.y0, sb.y1)
      const w = Math.abs(sb.x1 - sb.x0)
      const h = Math.abs(sb.y1 - sb.y0)
      ctx.fillStyle = 'rgba(140,200,255,0.12)'
      ctx.fillRect(x, y, w, h)
      ctx.strokeStyle = 'rgba(170,215,255,0.9)'
      ctx.lineWidth = 1
      ctx.strokeRect(x + 0.5, y + 0.5, w, h)
    }

    // connection lost: cold desaturating veil
    if (!st.connected) {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      ctx.fillStyle = 'rgba(30,38,52,0.45)'
      ctx.fillRect(0, 0, cw, ch)
    }

    requestAnimationFrame(loop)
  }
  requestAnimationFrame(loop)
}

// ---- territory layer -------------------------------------------------------------------

function drawTerritory(
  ctx: CanvasRenderingContext2D, st: GameState, visChunks: string[], z: number,
  minX: number, minY: number, maxX: number, maxY: number,
): void {
  const fills = new Map<string, Path2D>()
  // Frontier tiles that need a border, kept per-tile (NOT batched) so each can be
  // stroked inside its own visible-top clip.
  const bordered: { q: number; r: number; color: string; edges: number[] }[] = []
  for (const key of visChunks) {
    const ck = st.chunks.get(key)
    if (!ck || ck.owners.size === 0) continue
    for (const [idx, owner] of ck.owners) {
      const q = ck.cx * CHUNK + (idx % CHUNK)
      const r = ck.cy * CHUNK + Math.floor(idx / CHUNK)
      const p = hexToPixel(q, r, S)
      if (p.x < minX - S * 2 || p.x > maxX + S * 2 || p.y < minY - S * 2 || p.y > maxY + S * 2) continue
      p.y -= elevLift(st, q, r) // ride the 2.5D terrain
      const color = st.factions.get(owner)?.color ?? '#b0a48c'
      let fp = fills.get(color)
      if (!fp) { fp = new Path2D(); fills.set(color, fp) }
      addHex(fp, p.x, p.y, S * 1.01)
      // crisp border where the neighbor's owner differs. A missing neighbor
      // chunk means "unknown" (not loaded), not "unowned" — skip it so we don't
      // stroke phantom borders along chunk seams.
      let edges: number[] | null = null
      for (let d = 0; d < 6; d++) {
        const nq = q + DIRS[d].q
        const nr = r + DIRS[d].r
        if (!chunkAt(st, nq, nr)) continue
        if (ownerAt(st, nq, nr) === owner) continue
        ;(edges ??= []).push(d)
      }
      if (edges) bordered.push({ q, r, color, edges })
    }
  }
  ctx.globalAlpha = 0.13
  for (const [color, path] of fills) {
    ctx.fillStyle = color
    ctx.fill(path)
  }
  // Borders clipped to each tile's visible top (the same even-odd occlusion the terrain
  // uses), so the frontier line hugs the terrace it belongs to and is hidden behind
  // taller hexes — instead of one flat line drawn straight down a cliff, across the
  // hexes below it.
  ctx.globalAlpha = 0.85
  ctx.lineWidth = 2 / z
  ctx.lineCap = 'round'
  for (const b of bordered) {
    const p = hexToPixel(b.q, b.r, S)
    p.y -= elevLift(st, b.q, b.r)
    ctx.save()
    clipVisibleTopAbs(ctx, st, b.q, b.r)
    ctx.strokeStyle = b.color
    ctx.beginPath()
    for (const d of b.edges) {
      const e = hexEdge(p.x, p.y, d, S, 0.485)
      ctx.moveTo(e.x1, e.y1)
      ctx.lineTo(e.x2, e.y2)
    }
    ctx.stroke()
    ctx.restore()
  }
  ctx.globalAlpha = 1
}

function addHex(path: Path2D, x: number, y: number, size: number): void {
  for (let i = 0; i < 6; i++) {
    const a = (Math.PI / 180) * (60 * i - 90)
    const px = x + size * Math.cos(a)
    const py = y + size * Math.sin(a)
    if (i === 0) path.moveTo(px, py)
    else path.lineTo(px, py)
  }
  path.closePath()
}
